import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import './Config/app/i18n';
import './Config/api/httpClient.config';
import './Domain/style/app.css';
import './Domain/style/legacy.scss';
import { initPostHog } from './Config/app/initPostHog';
import { mainRouter } from './Config/app/router/main.router';
import { initOpenAiPixel, measureOpenAiConversion } from './Config/libs/openAiPixel';
import { ThemeProvider } from './Domain/components/ThemeProvider';

// The Django shell drops a single JSON script with the deployed bundle version +
// environment. Read it once at boot; everything else comes from the bootstrap
// endpoint after authentication.
interface FrontendConfig {
  version: string;
  dd_env: string;
}

function readFrontendConfig(): FrontendConfig {
  const el = document.getElementById('frontend-config');
  if (!el?.textContent) return { version: 'unknown', dd_env: '' };
  try {
    return JSON.parse(el.textContent) as FrontendConfig;
  } catch {
    return { version: 'unknown', dd_env: '' };
  }
}

const frontendConfig = readFrontendConfig();

// --- Datadog RUM (prod only) -------------------------------------------------
if (frontendConfig.dd_env === 'prod') {
  const rumScript = document.createElement('script');
  rumScript.src = 'https://www.datadoghq-browser-agent.com/eu1/v4/datadog-rum.js';
  rumScript.async = true;
  rumScript.onload = () => {
    window.DD_RUM?.init({
      clientToken: 'pub5de2760b59baac71d29e8c4e3199929a',
      applicationId: 'd93e3e82-391c-403d-b460-dabf0bcee15f',
      site: 'datadoghq.eu',
      service: 'app-quickchat',
      version: frontendConfig.version,
      env: frontendConfig.dd_env,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
    });
    window.DD_RUM?.startSessionReplayRecording();
  };
  document.head.appendChild(rumScript);
}

// --- PostHog + OAuth attribution --------------------------------------------
// OAuth attribution (posthog_oauth_tracking) is handled once by
// processOAuthTracking() inside initPostHog(), gated on VITE_PUBLIC_POSTHOG_KEY.
initPostHog();
initOpenAiPixel();

// `?account_activated=1` is set by the backend email-activation endpoint on the
// first successful flip; fire the ad-network conversion once, then strip the
// param so a refresh doesn't re-fire.
const activationUrl = new URL(window.location.href);
if (activationUrl.searchParams.get('account_activated') === '1') {
  const regProps = { event_category: 'User Registration', auth_method: 'email' };
  const dl = (window.dataLayer = window.dataLayer || []);
  dl.push({ event: 'account_created', ...regProps });
  const hsq = (window._hsq = window._hsq || []);
  hsq.push(['trackCustomBehavioralEvent', { name: 'account_created', properties: regProps }]);
  measureOpenAiConversion('registration_completed');

  activationUrl.searchParams.delete('account_activated');
  window.history.replaceState(
    {},
    '',
    activationUrl.pathname + activationUrl.search + activationUrl.hash,
  );
}

// Preserve the legacy navigation bridge a few callers still use.
window.__reactRouterNavigate = (navigate) => {
  mainRouter.navigate(navigate);
};

// --- Mount -------------------------------------------------------------------
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('#root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <RouterProvider router={mainRouter} />
    </ThemeProvider>
  </StrictMode>,
);
